import { useState, useCallback, useEffect } from 'react';
import { fetchEvents, fetchExternalEvents } from '../utils/eventApi';
import { MOBILIZE_EVENTS_ENABLED } from '../utils/featureFlags';
import { normalizeLocationParams } from '../utils/locationUtils';
import { sortEventsByNearZip } from '../utils/eventProximity';

/**
 * Shared events filter + fetch logic used by both the Events list page and the
 * Events map page so the two views stay in sync and behave identically.
 *
 * Owns the filter state (event type, location search, RSVP status, "use my
 * location") plus the events list, loading and error state, and the loadEvents
 * routine (including the sort + recurring-series dedupe both views rely on).
 *
 * Page-specific presentation state (Places autocomplete UI, geolocation
 * resolution, toasts) stays in the page component.
 */

const NATIVE_FETCH_PAGE_SIZE = 100;
const MAX_MERGED_EVENTS = 150;

export function buildEventParams({
  selectedEventType,
  submittedLocationSearch,
  useCurrentLocation,
  rsvpFilter,
  organizerId,
  isLoggedIn,
  bounds,
  nearZip = '',
  pageSize = NATIVE_FETCH_PAGE_SIZE,
}) {
  const params = {};
  if (selectedEventType) params.event_type = selectedEventType;

  const trimmedSearch = (submittedLocationSearch || '').trim();
  if (trimmedSearch) {
    Object.assign(params, normalizeLocationParams(trimmedSearch));
  }

  if (useCurrentLocation && isLoggedIn && !trimmedSearch) {
    params.use_current_location = 'true';
  }
  if (rsvpFilter && isLoggedIn) params.my_rsvp_status = rsvpFilter;
  if (organizerId) params.organizer_id = organizerId;

  const normalizedNearZip = String(nearZip || '').trim().slice(0, 5);
  if (/^\d{5}$/.test(normalizedNearZip)) {
    params.near_zip = normalizedNearZip;
  }

  if (bounds) {
    params.sw_lat = bounds.south;
    params.sw_lng = bounds.west;
    params.ne_lat = bounds.north;
    params.ne_lng = bounds.east;
  }

  params.page_size = pageSize;
  return params;
}

function sortAndDedupe(list) {
  const sorted = [...list].sort(
    (a, b) => new Date(a.start_datetime || 0) - new Date(b.start_datetime || 0),
  );
  const seen = new Set();
  return sorted.filter((ev) => {
    if (ev.is_recurring && ev.series_id) {
      if (seen.has(ev.series_id)) return false;
      seen.add(ev.series_id);
    }
    return true;
  });
}

function capMergedEvents(list, maxEvents, nearZipAnchor = null) {
  const merged = sortAndDedupe(list);
  const sorted = nearZipAnchor ? sortEventsByNearZip(merged, nearZipAnchor) : merged;
  if (sorted.length <= maxEvents) return sorted;
  return sorted.slice(0, maxEvents);
}

/**
 * @param {object|null} bounds Optional map viewport ({ south, west, north, east }).
 *   Only geocoded events inside it are returned. Keep this referentially stable
 *   (state, not a literal) — a new object identity triggers a refetch.
 */
export function useEventsFilters({
  organizerId = '',
  isLoggedIn = false,
  initialFilters = {},
  bounds = null,
  nearZip: nearZipProp = '',
  maxEvents = MAX_MERGED_EVENTS,
  pageSize = NATIVE_FETCH_PAGE_SIZE,
} = {}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [externalPartial, setExternalPartial] = useState(false);
  const [error, setError] = useState(null);

  const [selectedEventType, setSelectedEventType] = useState(initialFilters.event_type || '');
  const [locationSearchInput, setLocationSearchInput] = useState(initialFilters.locationSearch || '');
  const [submittedLocationSearch, setSubmittedLocationSearch] = useState(initialFilters.locationSearch || '');
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const [rsvpFilter, setRsvpFilter] = useState(initialFilters.rsvp || '');
  const [civixOnly, setCivixOnly] = useState(initialFilters.civix_only === 'true');
  const nearZip = String(nearZipProp || initialFilters.nearZip || '').trim().slice(0, 5);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    setExternalPartial(false);
    try {
      const params = buildEventParams({
        selectedEventType,
        submittedLocationSearch,
        useCurrentLocation,
        rsvpFilter,
        organizerId,
        isLoggedIn,
        bounds,
        pageSize,
        nearZip,
      });

      const skipExternal =
        !MOBILIZE_EVENTS_ENABLED ||
        civixOnly ||
        Boolean(organizerId) ||
        Boolean(rsvpFilter);
      const externalParams = { ...params };
      delete externalParams.organizer_id;
      delete externalParams.use_current_location;
      delete externalParams.my_rsvp_status;
      delete externalParams.page_size;

      const useNearZip = /^\d{5}$/.test(nearZip);

      // Native events first so the page is usable while Mobilize warms its cache.
      const nativePayload = await fetchEvents(params, { returnMeta: useNearZip });
      const nativeList = nativePayload.results || [];
      const nearZipAnchor = nativePayload.nearZipAnchor || null;
      setEvents(capMergedEvents(nativeList, maxEvents, nearZipAnchor));
      setLoading(false);

      if (!skipExternal) {
        setLoadingMore(true);
        try {
          const { results: externalList, partial } = await fetchExternalEvents(externalParams);
          setEvents(capMergedEvents(
            [...nativeList, ...externalList],
            maxEvents,
            nearZipAnchor,
          ));
          setExternalPartial(partial);
        } catch (err) {
          console.warn('External events fetch failed:', err);
          setExternalPartial(true);
        } finally {
          setLoadingMore(false);
        }
      }
    } catch (err) {
      const msg = err?.message || 'Failed to load events';
      const isHtml = typeof msg === 'string' && /<\s*(\w|!)/i.test(msg);
      setError(isHtml ? 'Unable to load events. Please try again.' : msg);
      setEvents([]);
      setLoading(false);
      setLoadingMore(false);
    }
  }, [
    selectedEventType,
    submittedLocationSearch,
    useCurrentLocation,
    rsvpFilter,
    organizerId,
    isLoggedIn,
    civixOnly,
    bounds,
    maxEvents,
    pageSize,
    nearZip,
  ]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const getFilterQueryParams = useCallback(() => {
    const qs = new URLSearchParams();
    if (selectedEventType) qs.set('event_type', selectedEventType);
    const trimmed = (submittedLocationSearch || '').trim();
    if (trimmed) qs.set('location_search', trimmed);
    if (rsvpFilter) qs.set('rsvp', rsvpFilter);
    if (organizerId) qs.set('organizer', organizerId);
    if (civixOnly) qs.set('civix_only', 'true');
    if (/^\d{5}$/.test(nearZip)) qs.set('near_zip', nearZip);
    return qs;
  }, [selectedEventType, submittedLocationSearch, rsvpFilter, organizerId, civixOnly, nearZip]);

  return {
    events,
    loading,
    loadingMore,
    externalPartial,
    error,
    selectedEventType,
    setSelectedEventType,
    locationSearchInput,
    setLocationSearchInput,
    submittedLocationSearch,
    setSubmittedLocationSearch,
    useCurrentLocation,
    setUseCurrentLocation,
    rsvpFilter,
    setRsvpFilter,
    civixOnly,
    setCivixOnly,
    loadEvents,
    getFilterQueryParams,
  };
}
